import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { useMutation } from 'react-query';
import { getUser } from '@frontend/auth-helpers';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { dedupeInboxTagsArray } from '../../utils';
import { isSmsMessageItem, isCallRecordItem } from '../../utils/thread-items-utils';
import { useUnifiedThreadUpdater } from '../query-updaters';
import { SchemaSMSDataV4Service } from '../service';
import { ApplyTagIO } from '../types';

export const getApplyTagMutationKey = () => ['V4', 'ApplyTag'] as const;

type MutationContext<C = unknown> = {
  mutationContext: { didOptimisticUpdate: boolean; revertOptimisticUpdate?: () => void };
  otherContext?: C;
};

type UseApplyTagMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends ApplyTagIO['input'] = ApplyTagIO['input']
> = {
  options?: LimitedSchemaMutationOptions<ApplyTagIO, E, C, never, RequestOverride>;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `ApplyTag` mutation endpoint.
 * It handles unified thread cache updates with optimistic updates.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `true`.
 */
export const useApplyTagMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends ApplyTagIO['input'] = ApplyTagIO['input']
>({
  options,
  httpOptions,
  optimisticUpdate = true,
}: UseApplyTagMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const user = getUser();
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions });

  return useMutation<ApplyTagIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getApplyTagMutationKey(),
    mutationFn: (req) => SchemaSMSDataV4Service.ApplyTag(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      const mutationContext: MutationContext<C>['mutationContext'] = {
        didOptimisticUpdate: false,
      };

      if (optimisticUpdate) {
        // Use unified thread updater for optimistic updates
        const upsertThreadItemResult = await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: request.id,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (threadItem) => {
            if (!threadItem) return threadItem;

            if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
              const existingTagIds = threadItem.smsMessage.tags || [];
              const tagExists = existingTagIds.includes(request.tagId);
              const newTagIds = tagExists ? existingTagIds : [...existingTagIds, request.tagId];
              return {
                ...threadItem,
                smsMessage: {
                  ...threadItem.smsMessage,
                  tags: newTagIds,
                  tagsDetailed: newTagIds.map<NonNullable<typeof threadItem.smsMessage.tagsDetailed>[number]>(
                    (tagId) => ({
                      tagId,
                      smsId: threadItem.smsMessage.id,
                      smsCreatedAt: threadItem.smsMessage.createdAt,
                      appliedBy: user?.userID ?? '',
                    })
                  ),
                },
              };
            }
            if (isCallRecordItem(threadItem) && threadItem.callRecord) {
              const existingTags = threadItem.callRecord.tags || [];
              const newTag = {
                tagId: request.tagId,
                entityId: threadItem.callRecord.threadCallId,
                entityType: EntityType.ENTITY_TYPE_CALL,
                entityCreatedAt: new Date().toISOString(),
                appliedBy: user?.userID ?? '',
              };
              const tagExists = existingTags.some((tag) => tag.tagId === request.tagId);
              return {
                ...threadItem,
                callRecord: {
                  ...threadItem.callRecord,
                  tags: tagExists ? existingTags : [...existingTags, newTag],
                },
              };
            }
            return threadItem;
          },
          isOptimistic: true,
          getThreadOnly: true,
        });

        const upsertUnifiedThreadResult = await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (thread) =>
            thread
              ? {
                  ...thread,
                  uniqueTags: dedupeInboxTagsArray([
                    ...(thread.uniqueTags ?? []),
                    {
                      entityId: request.id,
                      tagId: request.tagId,
                      entityCreatedAt: request.createdAt,
                      appliedBy: user?.userID ?? '',
                      entityType: EntityType.ENTITY_TYPE_SMS,
                    },
                  ]),
                }
              : undefined,
          isOptimistic: true,
        });

        mutationContext.didOptimisticUpdate = true;
        mutationContext.revertOptimisticUpdate = () => {
          upsertThreadItemResult.revertCacheUpdate();
          upsertUnifiedThreadResult.revertCacheUpdate();
        };
      }

      return {
        mutationContext,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: async (response, request, context) => {
      if (!context?.mutationContext.didOptimisticUpdate) {
        // Apply changes if optimistic update wasn't done
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: request.id,
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (threadItem) => {
            if (!threadItem) return threadItem;

            if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
              const existingTagIds = threadItem.smsMessage.tags || [];
              const tagExists = existingTagIds.includes(request.tagId);
              const newTagIds = tagExists ? existingTagIds : [...existingTagIds, request.tagId];
              return {
                ...threadItem,
                smsMessage: {
                  ...threadItem.smsMessage,
                  tags: newTagIds,
                  tagsDetailed: newTagIds.map<NonNullable<typeof threadItem.smsMessage.tagsDetailed>[number]>(
                    (tagId) => ({
                      tagId,
                      smsId: threadItem.smsMessage.id,
                      smsCreatedAt: threadItem.smsMessage.createdAt,
                      appliedBy: user?.userID ?? '',
                    })
                  ),
                },
              };
            }
            if (isCallRecordItem(threadItem) && threadItem.callRecord) {
              const existingTags = threadItem.callRecord.tags || [];
              const newTag = {
                tagId: request.tagId,
                entityId: threadItem.callRecord.threadCallId,
                entityType: EntityType.ENTITY_TYPE_CALL,
                entityCreatedAt: new Date().toISOString(),
                appliedBy: user?.userID ?? '',
              };
              const tagExists = existingTags.some((tag) => tag.tagId === request.tagId);
              return {
                ...threadItem,
                callRecord: {
                  ...threadItem.callRecord,
                  tags: tagExists ? existingTags : [...existingTags, newTag],
                },
              };
            }
            return threadItem;
          },
          isOptimistic: false,
        });

        await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: request.threadId,
            locationId: request.locationId,
          },
          updater: (thread) =>
            thread
              ? {
                  ...thread,
                  uniqueTags: dedupeInboxTagsArray([
                    ...(thread.uniqueTags ?? []),
                    {
                      entityId: request.id,
                      tagId: request.tagId,
                      entityCreatedAt: request.createdAt,
                      appliedBy: user?.userID ?? '',
                      entityType: EntityType.ENTITY_TYPE_SMS,
                    },
                  ]),
                }
              : undefined,
          isOptimistic: false,
        });
      }

      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      if (context?.mutationContext.didOptimisticUpdate) {
        context.mutationContext.revertOptimisticUpdate?.();
      }

      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
